// ── 로또 번호 조합 공통 검증·안전 생성 ─────────────────────────────────────
// 모든 생성기(랜덤/행운/스마트 추천)가 동일한 불변식을 공유한다.
//   - 본 번호: 정확히 6개, 모두 정수 1~45, 서로 중복 없음
//   - 보너스: 정수 1~45, 본 번호에 포함되지 않음
// 검증 통과 시 정렬된 복사본을 반환하며, 입력 배열을 직접 변경하지 않는다.

export interface LottoCombination {
  numbers: number[];
  bonus: number;
}

export type LottoCombinationValidation =
  | { valid: true; combination: LottoCombination }
  | { valid: false; reason: string };

const MIN_NUMBER = 1;
const MAX_NUMBER = 45;
const MAIN_COUNT = 6;

/**
 * 본 번호 + 보너스 조합을 검증한다.
 * - numbers: 정확히 6개, 정수 1~45, 중복 없음
 * - bonus: 정수 1~45, 본 번호에 미포함
 * 유효하면 정렬된 복사본을 담은 combination 을 반환하고,
 * 유효하지 않으면 reason 을 반환한다. 입력 배열은 변경하지 않는다.
 */
export function validateLottoCombination(
  numbers: readonly number[],
  bonus: number,
): LottoCombinationValidation {
  if (!Array.isArray(numbers)) {
    return { valid: false, reason: '본 번호는 배열이어야 합니다.' };
  }
  if (numbers.length !== MAIN_COUNT) {
    return { valid: false, reason: '본 번호는 정확히 6개여야 합니다.' };
  }

  const seen = new Set<number>();
  for (const n of numbers) {
    if (typeof n !== 'number' || !Number.isInteger(n) || n < MIN_NUMBER || n > MAX_NUMBER) {
      return { valid: false, reason: '본 번호는 1~45 사이의 정수여야 합니다.' };
    }
    if (seen.has(n)) {
      return { valid: false, reason: '본 번호는 서로 달라야 합니다.' };
    }
    seen.add(n);
  }

  if (typeof bonus !== 'number' || !Number.isInteger(bonus) || bonus < MIN_NUMBER || bonus > MAX_NUMBER) {
    return { valid: false, reason: '보너스 번호는 1~45 사이의 정수여야 합니다.' };
  }
  if (seen.has(bonus)) {
    return { valid: false, reason: '보너스 번호는 본 번호와 달라야 합니다.' };
  }

  const sorted = [...numbers].sort((a, b) => a - b);
  return { valid: true, combination: { numbers: sorted, bonus } };
}

/**
 * 항상 유효한 조합을 반환하는 안전 랜덤 fallback.
 * Fisher-Yates 셔플로 1~45 를 섞은 뒤 앞 6개를 본 번호, 7번째를 보너스로 사용한다.
 * (결과는 구조상 항상 불변식을 만족한다.)
 */
export function generateSafeRandomCombination(): LottoCombination {
  const pool: number[] = [];
  for (let i = MIN_NUMBER; i <= MAX_NUMBER; i++) pool.push(i);

  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }

  const numbers = pool.slice(0, MAIN_COUNT).sort((a, b) => a - b);
  const bonus = pool[MAIN_COUNT];
  return { numbers, bonus };
}

/**
 * 생성 결과를 검증하고, 유효하지 않으면 안전한 랜덤 조합으로 대체한다.
 * 항상 유효한 조합을 반환하므로 UI 표시·DB 저장 직전에 안전하게 사용할 수 있다.
 */
export function validateOrFallback(
  numbers: readonly number[],
  bonus: number,
): LottoCombination {
  const result = validateLottoCombination(numbers, bonus);
  if (result.valid) return result.combination;
  return generateSafeRandomCombination();
}